import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { fetchJoinedClasses, type JoinedClassesPage } from '@/features/profile/api';
import type { ProfileJoinedClassListItem } from '@/features/profile/types';

interface JoinedClassesQueryOptions {
  enabled?: boolean;
  size?: number;
}

/**
 * GET /midl/api/v3/board/joined?userId=:userId&page=…&size=… — classes the
 * user has joined. Paginated; auto-fetches subsequent pages until the
 * reported `totalCount` is reached so the UI always renders the full list.
 */
export function useJoinedClassesQuery(
  userId: string | number | undefined,
  { enabled = true, size = 10 }: JoinedClassesQueryOptions = {},
) {
  const query = useInfiniteQuery<
    JoinedClassesPage,
    Error,
    { pages: JoinedClassesPage[]; pageParams: number[] },
    readonly unknown[],
    number
  >({
    queryKey: queryKeys.profile.joinedClasses(userId ?? '', 0, size),
    queryFn: ({ pageParam = 0 }) =>
      fetchJoinedClasses(userId as string | number, { page: pageParam, size }),
    enabled: enabled && Boolean(userId),
    initialPageParam: 0,
    staleTime: 60_000,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
      const total = lastPage.totalCount ?? 0;
      if (lastPage.items.length === 0) return undefined;
      if (total > 0 && fetched >= total) return undefined;
      if (lastPage.items.length < size) return undefined;
      return allPages.length;
    },
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const items = useMemo<ProfileJoinedClassListItem[]>(
    () => (query.data?.pages ?? []).flatMap((page) => [...page.items]),
    [query.data],
  );

  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0;

  return { ...query, items, totalCount };
}
